import {navigateTo} from '@typed-router';
import {storeToRefs} from 'pinia';
import {useAuth, useTranslation} from '#imports';
import {pushVJoySnackbar} from '@maltjoy/core-vue';
import {freelancerSignupEventBus} from '~/config/freelancer-signup-events';
import {FreelancerSignupFunctionalErrorCodeEnum} from '~/openapi';
import {useFreelancerSignupStore} from '../store/useFreelancerSignupStore';

export type ErrorWithFunctionalErrorCode = {cause: {response: {_data: {code: FreelancerSignupFunctionalErrorCodeEnum}}}};

export function useFreelancerSignupFunctionalError() {
    // Composables
    const auth = useAuth();
    const {t} = useTranslation();

    // Store
    const freelancerSignupStore = useFreelancerSignupStore();
    const {loggedIn} = storeToRefs(auth);

    async function askForRedirectionIfNeeded(error: ErrorWithFunctionalErrorCode): Promise<void> {
        if (isErrorRequiringToRedirect(error)) {
            const errorCode = getWorkflowFunctionalErrorCode(error);

            switch (errorCode) {
                case FreelancerSignupFunctionalErrorCodeEnum.AlreadyFreelancer:
                    await navigateTo('/profile');
                    break;
                case FreelancerSignupFunctionalErrorCodeEnum.NoWorkflowFound:
                    if (loggedIn.value) {
                        // the user probably signed in on another tab,
                        // otherwise their signup workflow should be findable by their connected account
                        await navigateTo('/', {external: true});
                    } else if (freelancerSignupStore.isFreelancerEmailDefined()) {
                        freelancerSignupEventBus.emit('freelancer-signup-step:go-to-welcome-back');
                    } else {
                        freelancerSignupEventBus.emit('freelancer-signup-step:go-to-first-step-to-complete');
                    }
                    break;
                case FreelancerSignupFunctionalErrorCodeEnum.UserShouldBeLoggedIn:
                    freelancerSignupEventBus.emit('freelancer-signup-step:go-to-signin');
                    break;
                case FreelancerSignupFunctionalErrorCodeEnum.WorkflowInvalidForCreation:
                    pushVJoySnackbar({
                        props: {
                            message: t('profile.signup.step.end-workflow.notification.error.redirect-to-step'),
                            level: 'warning',
                        },
                    });

                    await freelancerSignupStore.getMaybeWorkflow();

                    freelancerSignupEventBus.emit('freelancer-signup-step:go-to-first-step-to-complete');
                    break;
                default:
                    freelancerSignupEventBus.emit('freelancer-signup-step:go-to-first-step-to-complete');
                    break;
            }
        }
    }

    function getWorkflowFunctionalErrorCode(error: ErrorWithFunctionalErrorCode): FreelancerSignupFunctionalErrorCodeEnum | undefined {
        return error.cause.response._data.code;
    }

    function isErrorRequiringToRedirect(error: ErrorWithFunctionalErrorCode): boolean {
        return (
            isWorkflowFunctionalError(error, FreelancerSignupFunctionalErrorCodeEnum.NoWorkflowFound) ||
            isWorkflowFunctionalError(error, FreelancerSignupFunctionalErrorCodeEnum.AlreadyFreelancer) ||
            isWorkflowFunctionalError(error, FreelancerSignupFunctionalErrorCodeEnum.WorkflowInvalidForCreation) ||
            isWorkflowFunctionalError(error, FreelancerSignupFunctionalErrorCodeEnum.UserShouldBeLoggedIn)
        );
    }

    function isErrorWithFunctionalErrorCode(error: any): error is ErrorWithFunctionalErrorCode {
        return error?.statusCode === 422 && !!error?.cause?.response?._data.code;
    }

    function isWorkflowFunctionalError(error: ErrorWithFunctionalErrorCode, code: FreelancerSignupFunctionalErrorCodeEnum): boolean {
        return getWorkflowFunctionalErrorCode(error) === code;
    }

    return {
        askForRedirectionIfNeeded,
        getWorkflowFunctionalErrorCode,
        isErrorRequiringToRedirect,
        isErrorWithFunctionalErrorCode,
        isWorkflowFunctionalError,
    };
}
